import { useEffect, useRef, useState } from 'react';
import { pocketColor as colorOf, type RoulettePocket } from '@tv-casino/shared';
import { RING_RATIO, ballAngle, polar, segmentPath } from '@tv-casino/viewmodels/lib/wheelGeometry.js';
import styles from './RouletteWheel.module.css';

/** Where the ball rides the track, and where it settles, in view-box units. */
const TRACK_RADIUS = 92;
const POCKET_RADIUS = 80;

/** The share of the roll the ball spends out on the track before it drops. */
const DROP_AT = 0.55;

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

const toneClass = (tone: string): string =>
  tone === 'red'
    ? styles.pocketRed ?? ''
    : tone === 'black'
      ? styles.pocketBlack ?? ''
      : styles.pocketGreen ?? '';

export interface RouletteWheelProps {
  /** Pockets in wheel order, shared with the server's spin. */
  wheelOrder: readonly RoulettePocket[];
  /** Index of the landed pocket in `wheelOrder`, or null before the spin. */
  landedIndex: number | null;
  /** The pocket label once the ball is released; the plate waits for the land. */
  pocket: string | null;
  pocketColor: 'red' | 'black' | 'green' | null;
  /** What the table says once the spin has paid. */
  resultLine?: string | null;
  /** Server deadline the ball must land on. */
  endsAt: number | null;
  /** What to say before the first spin of a seat. */
  placeholder?: string;
  size?: 'phone' | 'tv';
}

/**
 * The shared roller: TV felt and phone both feed it the published pocket and
 * the dealer deadline, so they run the same wheel and neither can call a number
 * the server did not spin. The plate waits for the ball; the felt's dolly waits
 * for settle, which is a separate beat.
 */
export function RouletteWheel({
  wheelOrder,
  landedIndex,
  pocket,
  pocketColor,
  resultLine = null,
  endsAt,
  placeholder = 'Waiting on the next spin',
  size = 'phone',
}: RouletteWheelProps) {
  const [angle, setAngle] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rolling, setRolling] = useState(false);
  const cumulative = useRef(0);

  useEffect(() => {
    if (landedIndex === null) {
      cumulative.current = 0;
      setAngle(0);
      setDuration(0);
      setRolling(false);
      return;
    }
    const target = ballAngle(cumulative.current, landedIndex, wheelOrder.length);
    cumulative.current = target;
    const ms = Math.max(1_200, Math.min(5_500, (endsAt ?? Date.now() + 1_500) - Date.now()));
    setDuration(ms);
    setAngle(target);
    setRolling(true);
    const settle = window.setTimeout(() => setRolling(false), ms);
    return () => window.clearTimeout(settle);
  }, [landedIndex, pocket, wheelOrder.length, endsAt]);

  const landed = landedIndex !== null && !rolling;
  const radius = rolling || landedIndex === null ? TRACK_RADIUS : POCKET_RADIUS;
  const plateColor = pocketColor ?? (pocket != null ? colorOf(pocket as RoulettePocket) : null);

  return (
    <div className={cx(styles.wrap, size === 'tv' && styles.wrapTv)}>
      <div
        className={styles.stage}
        role="img"
        aria-label={
          landed && pocket !== null
            ? `The ball landed on ${pocket}`
            : rolling
              ? 'The ball is rolling'
              : placeholder
        }
      >
        {/*
          Layered, not one SVG: the ring and the ball each turn as a whole HTML
          box, so the compositor spins a finished picture instead of repainting
          37 frets, their numbers and a blurred shadow on every frame — the
          difference between a smooth spin and a slideshow on an older
          Chromecast. The shadow and hub sit still underneath.
        */}
        <div className={styles.wheel} aria-hidden="true">
          <svg className={styles.wheelBase} viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="52" className={styles.wheelHub} />
          </svg>
          <div
            className={styles.wheelRing}
            style={{
              transform: `rotate(${-angle * RING_RATIO}deg)`,
              transitionDuration: `${duration}ms`,
            }}
          >
            <svg className={styles.layer} viewBox="0 0 200 200">
              {wheelOrder.map((p, i) => (
                <path
                  key={i}
                  d={segmentPath(i, wheelOrder.length, 62, 97)}
                  className={toneClass(colorOf(p))}
                  stroke="rgba(232, 195, 122, 0.35)"
                  strokeWidth="0.6"
                />
              ))}
              {wheelOrder.map((p, i) => {
                const seg = 360 / wheelOrder.length;
                const [tx, ty] = polar(100, 100, 80, i * seg + seg / 2);
                return (
                  <text
                    key={`t${i}`}
                    x={tx}
                    y={ty}
                    className={styles.wheelNumber}
                    textAnchor="middle"
                    dominantBaseline="central"
                    transform={`rotate(${angle * RING_RATIO} ${tx} ${ty})`}
                  >
                    {String(p)}
                  </text>
                );
              })}
            </svg>
          </div>
          <div
            className={styles.ballArm}
            style={{ transform: `rotate(${angle}deg)`, transitionDuration: `${duration}ms` }}
          >
            <div
              className={styles.ballDrop}
              style={{
                // The box is 200 view-box units tall, so a unit is half a percent.
                transform: `translateY(${-radius / 2}%)`,
                transitionDuration: `${Math.round(duration * (1 - DROP_AT))}ms`,
                transitionDelay: `${Math.round(duration * DROP_AT)}ms`,
              }}
            >
              <svg className={styles.layer} viewBox="0 0 200 200">
                <circle
                  cx="100"
                  cy="100"
                  r="5.2"
                  className={cx(
                    styles.ballDot,
                    rolling && styles.ballRolling,
                    landed && styles.ballLanded,
                  )}
                />
              </svg>
            </div>
          </div>
        </div>
        <div className={styles.readout}>
          {landed && pocket !== null ? (
            <>
              <span className={cx(styles.pocketPlate, plateColor && toneClass(plateColor))}>
                {pocket}
              </span>
              {resultLine && <span className={styles.resultLine}>{resultLine}</span>}
            </>
          ) : (
            <span className={styles.idle}>{'—'}</span>
          )}
        </div>
      </div>
    </div>
  );
}
